<template>
  <div :class="['lang-select', inline ? 'lang-select--inline' : '']" ref="dropdown">

    <!-- Normal mode: button + dropdown -->
    <template v-if="!inline">
      <button class="lang-select__btn" @click="toggleDropdown" type="button">
        <img :src="currentLanguageFlag" alt="flag" class="lang-select__flag" />
      </button>
      <ul v-if="isOpen" class="lang-select__dropdown">
        <li
          v-for="lang in languages"
          :key="lang.code"
          @click="selectLanguage(lang.code)"
          :class="['lang-select__option', lang.code === currentLanguage ? 'lang-select__option--active' : '']"
        >
          <img :src="lang.flag" :alt="lang.label" class="lang-select__option-flag" />
          <span>{{ lang.label }}</span>
        </li>
      </ul>
    </template>

    <!-- Inline mode (mobile): flat row of flag buttons -->
    <template v-else>
      <div class="lang-select__inline">
        <button
          v-for="lang in languages"
          :key="lang.code"
          type="button"
          :class="['lang-select__inline-btn', lang.code === currentLanguage ? 'lang-select__inline-btn--active' : '']"
          @click="selectLanguage(lang.code)"
        >
          <img :src="lang.flag" :alt="lang.label" class="lang-select__option-flag" />
          <span>{{ lang.label }}</span>
        </button>
      </div>
    </template>

  </div>
</template>

<script>
export default {
  name: 'LanguageSelect',
  props: {
    responsive:      { type: Number,  required: true },
    currentLanguage: { type: String,  required: true },
    inline:          { type: Boolean, default: false },
  },
  data() {
    return {
      isOpen: false,
      currentLanguageFlag: '',
      languages: [
        { code: 'vi', label: 'Tiếng Việt', flag: 'https://songhancapital.com/assets/image/VietNam.png' },
        { code: 'en', label: 'English',    flag: 'https://songhancapital.com/assets/image/English.png' },
        { code: 'fr', label: 'Français',   flag: 'https://songhancapital.com/assets/image/France.png' },
        { code: 'es', label: 'Español',    flag: 'https://songhancapital.com/assets/image/Spanish.png' },
      ],
    };
  },
  methods: {
    toggleDropdown() { this.isOpen = !this.isOpen; },
    closeDropdown()  { this.isOpen = false; },
    selectLanguage(code) {
      try { localStorage.setItem('scrollY', String(window.scrollY || 0)); } catch {}
      localStorage.setItem('language', code);
      this.setCurrentLanguage(code);
      this.closeDropdown();
      this.$emit('languageChanged', code);
    },
    handleClickOutside(event) {
      if (this.$refs.dropdown && !this.$refs.dropdown.contains(event.target)) {
        this.closeDropdown();
      }
    },
    setCurrentLanguage(code) {
      const lang = this.languages.find(l => l.code === code);
      this.currentLanguageFlag = lang ? lang.flag : 'https://songhancapital.com/assets/image/VietNam.png';
    },
  },
  mounted() {
    const saved = localStorage.getItem('language') || 'vi';
    this.setCurrentLanguage(saved);
    document.addEventListener('click', this.handleClickOutside);
  },
  unmounted() {
    document.removeEventListener('click', this.handleClickOutside);
  },
  watch: {
    currentLanguage(val) { this.setCurrentLanguage(val); },
  },
};
</script>

<style scoped lang="scss">
.lang-select {
  position: relative;
  display: inline-block;
}

// ─── Normal mode ──────────────────────────────────────────────────────────────

.lang-select__btn {
  display: flex;
  align-items: center;
  background: #fff;
  border: 1px solid #e0d9ce;
  border-radius: 6px;
  padding: 5px 8px;
  cursor: pointer;
  transition: border-color .15s ease, box-shadow .15s ease;

  &:hover {
    border-color: #c8a246;
    box-shadow: 0 0 0 3px rgba(200, 162, 70, .08);
  }
}

.lang-select__flag {
  width: 28px;
  height: 19px;
  object-fit: cover;
  border-radius: 2px;
  display: block;
}

.lang-select__dropdown {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  box-sizing: border-box;
  background: #fff;
  border: 1px solid #e4ddd0;
  border-radius: 6px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, .10);
  padding: 4px 0;
  list-style: none;
  margin: 0;
  z-index: 9999;
  min-width: 160px;
  max-width: calc(100vw - 24px);
}

.lang-select__option {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 14px;
  cursor: pointer;
  white-space: nowrap;
  font-size: 13px;
  color: #3a3530;
  transition: background .12s ease;

  &:hover { background: #faf8f4; }

  &--active {
    background: rgba(200, 162, 70, .08);
    color: #c8a246;
    font-weight: 600;
  }
}

.lang-select__option-flag {
  width: 28px;
  height: 19px;
  object-fit: cover;
  border-radius: 2px;
  flex-shrink: 0;
}

// ─── Inline mode (mobile) ─────────────────────────────────────────────────────

.lang-select--inline {
  display: block;
  width: 100%;
}

.lang-select__inline {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.lang-select__inline-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 12px;
  background: #faf8f4;
  border: 1px solid #e4ddd0;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  color: #3a3530;
  font-family: var(--font-sans);
  transition: border-color .15s ease, background .15s ease, color .15s ease;

  img { width: 24px; height: 16px; object-fit: cover; border-radius: 2px; flex-shrink: 0; }

  &:hover {
    border-color: #c8a246;
    background: rgba(200, 162, 70, .06);
    color: #c8a246;
  }

  &--active {
    border-color: #c8a246;
    background: rgba(200, 162, 70, .1);
    color: #c8a246;
    font-weight: 600;
  }
}
</style>
